import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

function RecordOdometerDialog({ open, handleClose, maintenanceId, vehicleId, currentOdometer, currentHourMeter, setMaintenance, mode = 'maintenance' }) {
  const { t } = useTranslation();
  const [readingKm, setReadingKm] = useState('');
  const [readingHours, setReadingHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const warningTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      setReadingKm('');
      setReadingHours('');
      setError(null);
      setWarning(null);
    }
  }, [open]);

  // Show warning if new reading is lower than current (debounced 1 second)
  useEffect(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    setWarning(null);

    const warnings = [];

    if (readingKm && currentOdometer) {
      const newReading = parseInt(readingKm, 10);
      if (!isNaN(newReading) && newReading < currentOdometer) {
        warnings.push(t('odometer.warningLower', { new: newReading, previous: currentOdometer }));
      }
    }

    if (readingHours && currentHourMeter) {
      const newHours = parseInt(readingHours, 10);
      if (!isNaN(newHours) && newHours < currentHourMeter) {
        warnings.push(t('odometer.warningLowerHours', { new: newHours, previous: currentHourMeter }));
      }
    }

    if (warnings.length > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        setWarning(warnings.join('\n'));
      }, 1000);
    }

    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [readingKm, readingHours, currentOdometer, currentHourMeter, t]);

  const handleSubmit = async () => {
    if (!readingKm && !readingHours) {
      setError(t('odometer.atLeastOneRequired'));
      return;
    }

    const payload = { context: mode === 'maintenance' ? 'during' : 'other' };

    if (readingKm) {
      const reading = parseInt(readingKm, 10);
      if (isNaN(reading) || reading < 0) {
        setError(t('odometer.invalidReading'));
        return;
      }
      payload.reading_km = reading;
    }

    if (readingHours) {
      const hours = parseInt(readingHours, 10);
      if (isNaN(hours) || hours < 0) {
        setError(t('odometer.invalidReading'));
        return;
      }
      payload.reading_hours = hours;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      if (mode === 'maintenance' && maintenanceId) {
        response = await axios.post(`/api/maintenances/${maintenanceId}/record_odometer/`, payload);
      } else if (vehicleId) {
        response = await axios.post(`/api/vehicles/${vehicleId}/record-odometer/`, payload);
      }

      if (response?.data?.warning) {
        console.log('Warning:', response.data.warning);
      }

      if (setMaintenance && maintenanceId) {
        const maintenanceResponse = await axios.get(`/api/maintenances/${maintenanceId}/`);
        setMaintenance(maintenanceResponse.data);
      }

      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setWarning(null);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon color="primary" />
          {t('odometer.recordReading')}
        </Box>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, overflow: 'visible' }}>
        {(currentOdometer || currentHourMeter) && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
            }}
          >
            {t('odometer.currentReading')}:
            {currentOdometer && <strong>{currentOdometer.toLocaleString('fi-FI')} km</strong>}
            {currentOdometer && currentHourMeter && ' / '}
            {currentHourMeter && <strong>{currentHourMeter.toLocaleString('fi-FI')} h</strong>}
          </Typography>
        )}
        {!currentOdometer && !currentHourMeter && <Box sx={{ mt: 1 }} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label={t('odometer.readingKm')}
            type="number"
            value={readingKm}
            onChange={(e) => setReadingKm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (readingKm || readingHours)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />

          <TextField
            fullWidth
            label={t('odometer.readingHours')}
            type="number"
            value={readingHours}
            onChange={(e) => setReadingHours(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (readingKm || readingHours)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">h</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        {warning && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{
              mt: 2,
              borderRadius: '8px',
              '& .MuiAlert-message': {
                fontSize: '13px',
              },
            }}
          >
            {warning}
          </Alert>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2, fontSize: '13px' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={(!readingKm && !readingHours) || loading}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          {loading ? t('Saving...') : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecordOdometerDialog;
